import { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { OrganizationSwitcher } from "@clerk/react-router";
import { Settings, ChevronDown, Plug, Mail } from "lucide-react";
import { countActiveDeals } from "@app/types";
import { UserMenu } from "./UserMenu";
import { NotificationBell } from "./NotificationBell";
import { useInvestorPortfolio } from "@/hooks/useInvestorPortfolio";
import { useAssessmentRequests } from "@/hooks/useAssessmentRequests";
import { useTenant } from "@/contexts/TenantContext";
import { cn } from "@/lib/utils";

/**
 * A single segment of the investor Pipeline / Portfolio segmented control, with
 * an optional count pill. The active segment lifts onto a card surface with
 * primary text (so the two screens read as an obvious two-way switch, STA-324
 * item #1); inactive segments stay muted. The count is hidden (not the segment)
 * while its underlying query loads, so the segment never flashes a stale or
 * zero count.
 */
function NavTab({
  to,
  label,
  count,
  showCount,
  testId,
}: {
  to: string;
  label: string;
  count: number;
  showCount: boolean;
  testId: string;
}) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors",
          isActive
            ? "bg-card text-primary shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          {label}
          {showCount && (
            <span
              data-testid={`${testId}-count`}
              className={cn(
                "rounded-full px-1.5 py-px text-[10.5px] font-semibold",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-border text-muted-foreground",
              )}
            >
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function Header() {
  const { funds, isLoading: isLoadingPortfolio } = useInvestorPortfolio();
  const { requests, isLoading: isLoadingRequests } = useAssessmentRequests();
  const { currentTenant } = useTenant();
  const isInvestor = currentTenant?.tenantType === "investor";

  const pipelineCount = countActiveDeals(requests);
  const portfolioCount = funds.reduce(
    (total, fund) => total + fund.companies.length,
    0,
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(e.target as Node)
      ) {
        setSettingsOpen(false);
      }
    }
    if (settingsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [settingsOpen]);

  return (
    <header className="sticky top-0 z-40 bg-card/80 backdrop-blur-sm border-b border-border">
      <div className="flex items-center justify-between h-16 px-4 lg:px-8 gap-4">
        {/* Left: Logo + (investor-only) Pipeline / Portfolio nav tabs */}
        <div className="flex items-center gap-4 sm:gap-6 min-w-0">
          <Link
            to={isInvestor ? "/pipeline" : "/assessments"}
            data-testid="header-logo-link"
            className="flex-shrink-0 flex items-center"
          >
            <img src="/icon.svg" alt="StackVerify" className="h-8 sm:h-10" />
          </Link>

          {isInvestor && (
            <nav
              className="inline-flex items-center gap-1 rounded-lg border border-primary/15 bg-primary/5 p-1"
              data-testid="header-nav"
            >
              <NavTab
                to="/pipeline"
                label="Pipeline"
                count={pipelineCount}
                showCount={!isLoadingRequests}
                testId="header-tab-pipeline"
              />
              <NavTab
                to="/portfolio"
                label="Portfolio"
                count={portfolioCount}
                showCount={!isLoadingPortfolio}
                testId="header-tab-portfolio"
              />
            </nav>
          )}
        </div>

        {/* Center/Right: Org Switcher, Settings, and User Menu */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <OrganizationSwitcher
            hidePersonal
            afterSelectOrganizationUrl="/assessments"
            appearance={{
              elements: {
                rootBox: "flex items-center",
                organizationSwitcherTrigger:
                  "px-2 sm:px-3 py-2 border border-border rounded-md hover:bg-muted text-sm truncate",
              },
            }}
          />
          <NotificationBell />

          {/* Settings dropdown */}
          <div className="relative" ref={settingsRef}>
            <button
              type="button"
              onClick={() => setSettingsOpen(!settingsOpen)}
              className="flex items-center gap-0.5 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Settings"
            >
              <Settings className="h-5 w-5" />
              <ChevronDown className="h-3 w-3" />
            </button>

            {settingsOpen && (
              <div className="absolute right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                {!isInvestor && (
                  <Link
                    to="/settings/integrations"
                    onClick={() => setSettingsOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Plug className="h-4 w-4 text-muted-foreground" />
                    Integrations
                  </Link>
                )}
                <Link
                  to="/settings/invitations"
                  onClick={() => setSettingsOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Invitations
                </Link>
              </div>
            )}
          </div>

          <UserMenu />
        </div>
      </div>
    </header>
  );
}
